import React, { useEffect, useState } from "react";
import { Pagination } from "antd";
import { cities, categories } from "../../constants/commonConstants";
import ExploreJobCard from "../../components/Tasker/ExploreJobCard";
import Dropdown from "../../components/Form/Dropdown";
import { useSearchParams } from "react-router-dom";
import CustomButton from "../../components/Form/CustomButton";
import { RiSearchLine } from "react-icons/ri";
import { useThunk } from "../../hooks/useThunk";
import { useSelector } from "react-redux";
import { fetchAllJobs } from "../../store";
import Loader from "../../components/Common/Loader";
import ErrorDisplay from "../../error/ErrorDisplay";
import ExploreJobCardSkeleton from "../../components/skeleton/ExploreJobCardSkeleton";

const ExploreJobs = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { posts, pagination } = useSelector(
    (state) => state.jobPost?.allJobPosts || { posts: [], pagination: null }
  );

  const [doFetchJobs, isFetchingJobs, jobError] = useThunk(fetchAllJobs);

  // State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // selected city/category/skill:
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedSubCity, setSelectedSubCity] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [availableSubCities, setAvailableSubCities] = useState([]);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [isSearchTriggered, setIsSearchTriggered] = useState(false);
  const [firstTrigger, setFirstTrigger] = useState(true);

  const getSubCitiesByCity = (cityName) => {
    const matchedCity = cities.find((city) => city.name === cityName);
    const subcities = matchedCity ? matchedCity.subcities : [];
    return subcities;
  };

  const getSkillsByCategory = (categoryName) => {
    if (!categoryName) return [];
    const matchedCategory = categories.find((cat) => cat.name === categoryName);
    return matchedCategory ? matchedCategory.skills : [];
  };

  // Load initial state from URL params
  useEffect(() => {
    const cityParam = searchParams.get("city");
    const subCityParam = searchParams.get("subcity");
    const categoryParam = searchParams.get("category");
    const skillParam = searchParams.get("skill");
    const pageParam = searchParams.get("page");

    let shouldTriggerSearch = false;

    if (cityParam) {
      const city = cities.find((c) => c.name === cityParam);
      setSelectedCity(city);
      setAvailableSubCities(getSubCitiesByCity(cityParam));
      shouldTriggerSearch = true;
    }

    if (categoryParam) {
      const category = categories.find((c) => c.name === categoryParam);
      setSelectedCategory(category);

      // Get available skills for this category
      const skills = getSkillsByCategory(categoryParam);
      setAvailableSkills(skills);

      // If we have a skill param, find the corresponding skill
      if (skillParam) {
        const skill = skills.find((s) => s.name === skillParam);
        setSelectedSkill(skill);
      }

      shouldTriggerSearch = true;
    }

    if (subCityParam && cityParam) {
      const subCity = getSubCitiesByCity(cityParam).find(
        (sc) => sc.name === subCityParam
      );
      setSelectedSubCity(subCity);
    }

    if (pageParam) {
      setCurrentPage(Number(pageParam));
    }

    // Trigger data fetch on page load if we have URL params
    if (shouldTriggerSearch) {
      // Small delay to ensure state updates are processed
      setTimeout(() => {
        setIsSearchTriggered(true);
      }, 100);
    }
  }, []);

  // Update URL params when filters change
  useEffect(() => {
    if (!isSearchTriggered) return;

    const params = new URLSearchParams();

    if (selectedCity) params.set("city", selectedCity.name);
    if (selectedSubCity) params.set("subcity", selectedSubCity.name);
    if (selectedCategory) params.set("category", selectedCategory.name);
    if (selectedSkill) params.set("skill", selectedSkill.name);
    if (currentPage > 1) params.set("page", currentPage.toString());

    setSearchParams(params);
    setIsSearchTriggered(false);
  }, [
    selectedCity,
    selectedSubCity,
    selectedCategory,
    selectedSkill,
    currentPage,
    isSearchTriggered,
  ]);

  // Load jobs
  useEffect(() => {
    if (isSearchTriggered || firstTrigger) {
      loadJobs(currentPage);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      setIsSearchTriggered(false);
    } else {
      return;
    }
  }, [currentPage, isSearchTriggered, firstTrigger]);

  const loadJobs = async (page) => {
    const offset = (page - 1) * itemsPerPage;
    console.log("load jobs");
    const queryParams = {
      offset,
      limit: itemsPerPage,
      city: selectedCity?.name || null,
      subcity: selectedSubCity?.name || null,
      category: selectedCategory?.name || null,
      skill: selectedSkill?.name || null,
    };

    try {
      await doFetchJobs(queryParams);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Failed to fetch Jobs:", error);
    }
    setFirstTrigger(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setIsSearchTriggered(true);
  };

  // Filter handlers
  const handleSelectedCity = (city) => {
    if (city) {
      setSelectedCity(city);
      setSelectedSubCity(null); // Reset subcity when city changes

      // Find the city object and extract its subcities
      const selectedCityObj = cities.find((c) => c.name === city.name);
      setAvailableSubCities(selectedCityObj ? selectedCityObj.subcities : []);
    } else {
      setSelectedCity("");
      setSelectedSubCity("");
      setAvailableSubCities([]); // Reset subcity options
    }
  };

  const handleSelectedSubCity = (subCity) => {
    if (subCity) {
      setSelectedSubCity(subCity);
    } else {
      setSelectedSubCity("");
    }
  };

  const handleSelectedCategory = (category) => {
    if (category) {
      setSelectedCategory(category);
      setSelectedSkill(null); // Reset skill when category changes

      // Find the category object and extract its skills
      const selectedCategory = categories.find(
        (cat) => cat.name === category.name
      );
      setAvailableSkills(selectedCategory ? selectedCategory.skills : []);
    } else {
      setSelectedCategory("");
      setSelectedSkill("");
      setAvailableSkills([]);
    }
  };

  const handleSelectedSkill = (skill) => {
    if (skill) {
      setSelectedSkill(skill);
    } else {
      setSelectedSkill(skill);
    }
  };

  const handleSearchClick = () => {
    setCurrentPage(1);
    setIsSearchTriggered(true);
  };

  return (
    <div className="mt-12 mb-[88px] px-[20px] md:px-[40px] lg:px-[100px]">
      {/* Title */}
      <h1 className="font-nonito_sans font-bold text-level-2 md:text-level-1 leading-[47.5px] text-dark text-center">
        Explore Jobs
      </h1>

      {/* Filter Dropdowns */}
      <div className="mt-10 flex flex-wrap lg:flex-nowrap justify-center gap-2 w-full max-w-[62.5rem] mx-auto">
        {/* City */}
        <div className="w-full sm:w-[48%] md:w-1/3">
          <Dropdown
            options={cities}
            defaultOption={selectedCity}
            placeholder="City"
            idKey="name"
            displayKey="name"
            wrapperClassName="w-full h-[56px] relative rounded-[20px] font-inter font-medium text-level-5"
            filterClassName="max-h-[240px] overflow-y-auto scrollbar-no-radius"
            buttonClassName="px-10 border border-light-blue h-12 rounded-[20px] text-content"
            dropdownClassName="rounded-[22px] shadow-lg px-[6px]"
            optionClassName="px-10 py-3 hover:bg-gray-100 first:rounded-t-[20px] last:rounded-b-[20px] text-content"
            onSelect={handleSelectedCity}
          />
        </div>

        {/* SubCity Dropdown */}
        <div className="w-full sm:w-[48%] lg:w-1/3">
          <Dropdown
            options={availableSubCities}
            defaultOption={selectedSubCity}
            placeholder="Sub City"
            idKey="name"
            displayKey="name"
            disabled={!selectedCity}
            wrapperClassName="w-full h-[56px] relative rounded-[20px] font-inter font-medium text-level-5"
            filterClassName="max-h-[240px] overflow-y-auto scrollbar-no-radius"
            buttonClassName={`px-10 border border-light-blue h-12 rounded-[20px] text-content ${
              !selectedCity ? "opacity-50 cursor-not-allowed" : ""
            }`}
            dropdownClassName="rounded-[22px] shadow-lg px-[6px]"
            optionClassName="px-10 py-3 hover:bg-gray-100 first:rounded-t-[20px] last:rounded-b-[20px] text-content"
            onSelect={handleSelectedSubCity}
          />
        </div>

        {/* Category */}
        <div className="w-full sm:w-[48%] md:w-1/3">
          <Dropdown
            options={categories}
            defaultOption={selectedCategory}
            placeholder="Category"
            idKey="name"
            displayKey="name"
            wrapperClassName="w-full h-[56px] relative rounded-[20px] font-inter font-medium text-level-5"
            filterClassName="max-h-[240px] overflow-y-auto scrollbar-no-radius"
            buttonClassName="px-10 border border-light-blue h-12 rounded-[20px] text-content"
            dropdownClassName="rounded-[22px] shadow-lg px-[6px]"
            optionClassName="px-10 py-3 hover:bg-gray-100 first:rounded-t-[20px] last:rounded-b-[20px] text-content"
            onSelect={handleSelectedCategory}
          />
        </div>

        {/* Skills */}
        <div className="w-full sm:w-[48%] md:w-1/3">
          <Dropdown
            options={availableSkills}
            placeholder="Skills"
            defaultOption={selectedSkill}
            disabled={!selectedCategory}
            idKey="name"
            displayKey="name"
            wrapperClassName="w-full h-[56px] relative rounded-[20px] font-inter font-medium text-level-5"
            filterClassName="max-h-[240px] overflow-y-auto scrollbar-no-radius"
            buttonClassName={`px-10 border border-light-blue h-12 rounded-[20px] text-content ${
              !selectedCategory ? "opacity-50 cursor-not-allowed" : ""
            }`}
            dropdownClassName="rounded-[22px] shadow-lg px-[6px]"
            optionClassName="px-10 py-3 hover:bg-gray-100 first:rounded-t-[20px] last:rounded-b-[20px] text-content"
            onSelect={handleSelectedSkill}
          />
        </div>

        {/* Search Button */}
        <div className="w-full sm:w-[48%] md:w-1/3 lg:w-[15%]">
          <CustomButton
            buttonText="Search"
            icon={<RiSearchLine size={20} />}
            iconPosition="left"
            bgColor="bg-primary"
            borderColor="border-transparent"
            textWeight="font-bold text-sm"
            className="px-5 text-(var[--primary-text-color]) rounded-[0.75rem]"
            onClick={handleSearchClick}
            loading={isFetchingJobs}
          />
        </div>
      </div>

      {/* Job Cards */}
      {/* Loading State */}
      {isFetchingJobs ? (
        <div className="mt-[40px] flex flex-col gap-6 lg:gap-12 min-h-[300px]">
          {[...Array(5)].map((_, idx) => (
            <ExploreJobCardSkeleton key={idx} />
          ))}
        </div>
      ) : jobError ? (
        <div className="mt-[40px] max-w-[800px] mx-auto">
          <ErrorDisplay
            errorMessage={
              jobError?.message || "Failed to load jobs. Please try again."
            }
            buttonText="Try Again"
            onButtonClick={() => setIsSearchTriggered(true)}
          />
        </div>
      ) : (
        /* Service Pro Cards */
        <div className="mt-[40px] flex flex-col gap-6 lg:gap-12">
          {posts.length > 0 ? (
            <>
              {posts.map((job, index) => (
                <ExploreJobCard job={job} key={job.id || index} />
              ))}

              {/* Pagination */}
              <div className="flex justify-center mt-8">
                <Pagination
                  current={currentPage}
                  total={pagination?.totalDocuments || 0}
                  pageSize={itemsPerPage}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  className="font-inter"
                />
              </div>
            </>
          ) : (
            <div className="text-center py-10">
              <p className="text-lg text-gray-500 font-inter">
                No Jobs found for the selected filters.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExploreJobs;
